import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface Results {
  boards: { id: string; title: string; parentTitle: string | null }[];
  cards: { boardId: string; boardTitle: string; cardId: string; type: string; snippet: string }[];
}
const EMPTY: Results = { boards: [], cards: [] };

/** Global search over board titles and card text (Ctrl+/ or the header button). */
export function SearchDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState<Results>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setRes(EMPTY);
      return;
    }
    let alive = true;
    setBusy(true);
    const id = setTimeout(() => {
      api<Results>(`/api/search?q=${encodeURIComponent(query)}`)
        .then((r) => alive && (setRes(r), setActive(0)))
        .catch(() => alive && setRes(EMPTY))
        .finally(() => alive && setBusy(false));
    }, 180);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [q]);

  const items = [
    ...res.boards.map((b) => ({ key: `b:${b.id}`, to: `/b/${b.id}`, kind: "board" as const, b })),
    ...res.cards.map((c) => ({
      key: `c:${c.cardId}`,
      to: `/b/${c.boardId}?card=${c.cardId}`,
      kind: "card" as const,
      c,
    })),
  ];
  const go = (to: string) => {
    onClose();
    nav(to);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[10vh]"
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={t.search}
    >
      <div className="flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-md border border-border bg-panel shadow-panel">
        <input
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowDown")
              e.preventDefault(), setActive((a) => Math.min(items.length - 1, a + 1));
            else if (e.key === "ArrowUp") e.preventDefault(), setActive((a) => Math.max(0, a - 1));
            else if (e.key === "Enter" && items[active]) go(items[active]!.to);
          }}
          placeholder={t.searchPlaceholder}
          className="border-b border-border bg-transparent px-4 py-3 text-base text-text outline-none placeholder:text-dim"
        />
        <div className="overflow-y-auto py-1">
          {q.trim().length < 2 ? (
            <div className="px-4 py-3 text-xs text-dim">{t.searchHint}</div>
          ) : items.length === 0 && !busy ? (
            <div className="px-4 py-3 text-sm text-muted">{t.searchNoResults}</div>
          ) : (
            <>
              {res.boards.length ? (
                <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-dim">
                  {t.searchBoards}
                </div>
              ) : null}
              {items.map((it, i) =>
                it.kind === "board" ? (
                  <button
                    key={it.key}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(it.to)}
                    className={`block w-full px-4 py-2 text-left ${i === active ? "bg-panel-2" : ""}`}
                  >
                    <div className="text-sm text-text">▣ {it.b.title}</div>
                    {it.b.parentTitle ? <div className="text-xs text-dim">{it.b.parentTitle}</div> : null}
                  </button>
                ) : (
                  <div key={it.key}>
                    {i === res.boards.length ? (
                      <div className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-wide text-dim">
                        {t.searchCards}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(it.to)}
                      className={`block w-full px-4 py-2 text-left ${i === active ? "bg-panel-2" : ""}`}
                    >
                      <div className="line-clamp-2 text-sm text-text">{it.c.snippet}</div>
                      <div className="text-xs text-dim">
                        {it.c.boardTitle} · {it.c.type}
                      </div>
                    </button>
                  </div>
                ),
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Mount once per page: opens the dialog on Ctrl+/ (or Cmd+/) and on the mn:search event. */
export function useSearchHotkey(setOpen: (v: boolean) => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onEvt = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mn:search", onEvt);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mn:search", onEvt);
    };
  }, [setOpen]);
}

export function SearchButton({ className = "" }: { className?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent("mn:search"))}
      className={`rounded-sm border border-border px-2 py-1 text-xs text-muted hover:text-text ${className}`}
      title={`${t.search} (Ctrl+/)`}
    >
      {t.search}
    </button>
  );
}
